import {
    Yup,
    getBankFieldValidations,
    getBillingNameOptionalValidation,
    getBillingSameAsMailingValidations,
    getPaymentTypeValidations,
    getPaypalValidations,
    getBillingAddressValidations,
    getGiftAssistValidations,
    getMatchingGiftValidations,
    getShippingSameAsMailingValidations,
    getShippingAddressValidations,
    getShippingMethodValidations,
    getDonationAmountValidations,
} from "@qgiv/core-validation";

const getPrivacyOptionsValidation = (settings) => {
    const { isStandardDonationForm = 0 } = settings;

    if (isStandardDonationForm) {
        return {
            // Show my amount on public event pages checkbox
            Do_Not_Show_Name: Yup.bool().required(),
            // Show my name on public event pages checkbox
            Do_Not_Show_Amount: Yup.bool().required(),
        };
    }
    return {
        // Show my amount on public event pages checkbox
        Privacy_Display_Amount: Yup.bool().required(),
        // Show my name on public event pages checkbox
        Privacy_Display_Name: Yup.bool().required(),
    };
};
// -------------------------------------------------------------------------
// TODO: Pass dynamic field names to validation messages by using functions
// local to the components to get access to the dynamic CMS generated field
// name and passing them to the functions in this file. Masks also need to
// be added to inputs where relevant.
// -------------------------------------------------------------------------
// NOTE: The validation logic that is associated with this form is
//       structured as follows:
//       1. When possible reuse existing global level validation logic by
//       accessing the schema chunks have been accessed from in
//       qgiv/validation.js.
//       2. If there is no existing global level validation logic that is
//       relevant to the field that you are validating and the field that
//       you are validating is static (always visible) or relatively simple
//       (e.g. a piece of validation that is always optional) add the
//       validation logic as form level schemas in this file.
//       3. If there is no existing global level validation logic that is
//       relevant to the field that you are validating and the field that
//       you are validating is dynamic (only visible when certain conditions
//       are present) and sufficiently complex, add the logic associated
//       with validating the field as a field level function that is
//       imported from this file. The reason this is typically preferable is
//       that field level validation is only run when a field is visible.
//       This means that adding the validation logic as field validation
//       ensures that there is no need to recreate the often complex logic
//       that toggles the visibility of a field while writing the validation.
// -------------------------------------------------------------------------
// -------------------------------------------------------------------------
// MARK: Form level validation schema
// -------------------------------------------------------------------------

const getPaymentValidations = (validationSettings) => {
    // -------------------------------------------------------------------------
    // NOTE: No validations have been added for the following fields as the sign
    //       in button has not yet been built into the form. Once this happens
    //       validation for these fields will have to be added.
    //       1. Payment_Nickname
    //       2. Save_Payment
    //       3. Stored_Payment
    // -------------------------------------------------------------------------
    const {
        matchingRatio,
        shippingPhoneRequired,
        validateShippingDetails,
        validateShippingMethods,
        registrationHasCommitment,
    } = validationSettings;

    // flag that is passed to getBillingAddressValidations to bypass
    // displayOption check that pertains to form/event fields
    const isP2P = true;

    return {
        ...getBillingNameOptionalValidation(),
        ...getPaymentTypeValidations(),
        ...getBankFieldValidations(),
        ...getPaypalValidations(),
        ...getBillingSameAsMailingValidations(),
        // Passing empty object in place of displayedBillingFields object
        // which Form and Events utilize to determine the display settings
        // of the billing fields. P2P has no such functionality so we pass an
        // empty object and flag indicating we are in P2P which will bypass
        // this additional logic
        ...getBillingAddressValidations({}, isP2P),
        ...getGiftAssistValidations(),
        ...getMatchingGiftValidations(matchingRatio),
        ...getShippingSameAsMailingValidations(validateShippingDetails),
        ...getShippingAddressValidations(
            validateShippingDetails,
            shippingPhoneRequired,
        ),
        ...getShippingMethodValidations(validateShippingMethods),
        // Donation_Amount validation came in as part of OOC-QG-13476
        // However, we might want this to persist after the imask package
        // issue is resolved.
        ...getDonationAmountValidations(),
        ...getPrivacyOptionsValidation(validationSettings),
        Commitment_Agreement: registrationHasCommitment
            ? Yup.bool().oneOf(
                  [true],
                  "You'll need to agree to the above statement in order to register",
              )
            : Yup.bool().required(),
    };
};

const getPaymentPageValidations = (validationSettings) => {
    // -------------------------------------------------------------------------
    // NOTE: When the total for registration of $0, we do not validations for payment page
    //       except for checking for invalid donation amount (which could trigger $0 registration)
    // -------------------------------------------------------------------------
    const { total, registrationHasCommitment } = validationSettings;
    return Number(total) > 0 || registrationHasCommitment
        ? { ...getPaymentValidations(validationSettings) }
        : { ...getDonationAmountValidations() };
};

export const getValidationSchema = (validationSettings) => {
    const validationShape = {
        ...getPaymentPageValidations(validationSettings),
    };
    const schema = Yup.object().shape(validationShape);
    return schema;
};
